import { io, type Socket } from 'socket.io-client';

/**
 * Lifecycle status for an AI job. Mirrors the API's `AiJobStatus`.
 */
export type AiJobStatus = 'queued' | 'running' | 'completed' | 'failed';

/**
 * The kind of work an AI job does. Mirrors the API's `AiJobAction`.
 */
export type AiJobAction =
  | 'generate-descriptions'
  | 'generate-voiceovers'
  | 'generate-translations'
  | 'generate-translation-voiceovers'
  | 'regenerate-translation'
  | 'generate-content'
  | 'generate-conclusion';

/**
 * The kind of entity an AI job operates on. Mirrors the API's `AiJobEntityType`.
 */
export type AiJobEntityType =
  | 'attraction'
  | 'attraction-theme'
  | 'tour'
  | 'attraction-translation'
  | 'attraction-theme-translation'
  | 'tour-translation';

export interface AiJobScope {
  entityType: AiJobEntityType;
  entityId: string;
  parentEntityId?: string;
  action: AiJobAction;
}

export interface AiJobState {
  jobId: string;
  scope: AiJobScope;
  status: AiJobStatus;
  startedAt: string;
  finishedAt?: string;
  error?: string;
  userId: string;
  organizationId: string;
}

const NAMESPACE_PATH = '/ai-job';

/**
 * Lazily-initialised singleton socket connection to the `/ai-job` namespace.
 * One connection serves the per-button hook, the per-dialog hook, and the
 * global notification provider — they each emit their own room subscriptions
 * over the same socket.
 */
let socketPromise: Promise<Socket> | null = null;

/**
 * Returns (and lazily creates) the singleton socket. Caller supplies a
 * `getToken` function; only the first call's token is used to authenticate
 * the underlying connection. After that the socket auto-reconnects with the
 * same auth payload — Clerk tokens rotate so the auth function should always
 * be fresh-callable.
 */
export function getAiJobSocket(
  getToken: () => Promise<string | null>,
): Promise<Socket> {
  if (socketPromise) return socketPromise;

  socketPromise = (async () => {
    const token = await getToken();
    const socket = io(NAMESPACE_PATH, {
      // Re-evaluate the token on every (re)connect so a long-lived tab
      // doesn't get stuck with an expired Clerk JWT.
      auth: async (cb: (data: { token: string | null }) => void) => {
        const fresh = await getToken();
        cb({ token: fresh ?? token });
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    return socket;
  })();

  return socketPromise;
}

/**
 * Tear down the singleton socket. Currently only used in tests / hot reload.
 */
export function disposeAiJobSocket(): void {
  if (!socketPromise) return;
  void socketPromise.then((s) => s.disconnect());
  socketPromise = null;
}
